import { useMutation } from '@tanstack/react-query'
import { useOrgIntegrationsApi } from 'apiProviders'
import { useUserData } from 'auth'
import Input from 'components/input'
import Loading from 'components/Loading'
import XeroItemAutocomplete from 'components/xeroItemAutocomplete'
import { useOrgId } from 'helpers'
import xeroImg from 'images/xero/Logo - Blue.png'
import { useState } from 'react'
import Button from 'components/button'
import { Text, View, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'

const Xero = () => {
    const trpc = useTRPC()
    const { user } = useUserData()
    const orgId = useOrgId() ?? ''
    const integrationsApi = useOrgIntegrationsApi()

    const [loading, setLoading] = useState(false)
    const [conjAccountIdLoading, setConjAccountIdLoading] = useState(false)

    const disconnectXeroMutation = useMutation(trpc.xero.disconnectXero.mutationOptions())
    const updateConfigMutation = useMutation(trpc.xero.updateConfig.mutationOptions())
    const getConnectStateMutation = useMutation(trpc.xero.getConnectState.mutationOptions())

    const xero = integrationsApi.data?.xero

    const redirect_uri = import.meta.env.DEV
        ? 'https://qchkocg5lj.execute-api.ap-southeast-2.amazonaws.com/xero/authredirect'
        : `https://api.${import.meta.env.VITE_APP_STAGE == 'prod' ? '' : import.meta.env.VITE_APP_STAGE + '.'}buyerflo.com.au/xero/authredirect`

    return (
        <View maxWidth={500}>
            <YStack gap="$4">
                <View
                    height={120}
                    width={120}
                >
                    <img
                        src={xeroImg}
                        style={{ height: '120px', width: '120px' }}
                    />
                </View>
                {xero ? (
                    <View data-tour="xero-connect">
                        <Text>Connected to {xero.tenants[0].tenantName}</Text>
                        <Button
                            marginTop="$4"
                            variant="outlined"
                            theme="red"
                            style={{ maxWidth: '150px' }}
                            onPress={async () => {
                                await disconnectXeroMutation.mutateAsync()
                                await integrationsApi.refresh()
                            }}
                        >
                            Disconnect
                        </Button>
                        <YStack
                            marginTop="$4"
                            gap="$4"
                            maxWidth={400}
                            data-tour="xero-config"
                        >
                            <XeroItemAutocomplete
                                label="Engagement Resource"
                                value={xero.config?.engagementResource}
                                loading={conjAccountIdLoading}
                                onChange={async (e, value) => {
                                    setConjAccountIdLoading(true)
                                    await updateConfigMutation.mutateAsync({
                                        engagementResource: value,
                                    })
                                    await integrationsApi.refresh()
                                    setConjAccountIdLoading(false)
                                }}
                            />
                            <XeroItemAutocomplete
                                label="Commission Resource"
                                value={xero.config?.commissionResource}
                                loading={conjAccountIdLoading}
                                onChange={async (e, value) => {
                                    setConjAccountIdLoading(true)
                                    await updateConfigMutation.mutateAsync({
                                        commissionResource: value,
                                    })
                                    await integrationsApi.refresh()
                                    setConjAccountIdLoading(false)
                                }}
                            />
                            <Input
                                label="Engagement Fee Delay (hours)"
                                helperText="Hours to delay sending engagement fee invoices after form completion. 0 = send immediately."
                                type="number"
                                value={String(xero.config?.engagementFeeDelay ?? 0)}
                                onChange={async (value: any) => {
                                    const numValue = parseInt(value) || 0
                                    setConjAccountIdLoading(true)
                                    await updateConfigMutation.mutateAsync({
                                        engagementFeeDelay: numValue > 0 ? numValue : undefined,
                                    })
                                    await integrationsApi.refresh()
                                    setConjAccountIdLoading(false)
                                }}
                            />
                        </YStack>
                    </View>
                ) : (
                    <Button
                        disabled={loading}
                        maxWidth="150px"
                        backgroundColor="$green8"
                        data-tour="xero-connect"
                        data-help-id="settings-xero-connect"
                        onPress={async () => {
                            setLoading(true)
                            try {
                                // Server-issued signed state (CSRF protection); the
                                // redirect handler verifies it before storing tokens.
                                const { state } = await getConnectStateMutation.mutateAsync()
                                const url = new URL('https://login.xero.com/identity/connect/authorize')
                                url.searchParams.set('response_type', 'code')
                                url.searchParams.set('client_id', import.meta.env.VITE_XERO_CLIENT_ID)
                                url.searchParams.set('redirect_uri', redirect_uri)
                                url.searchParams.set(
                                    'scope',
                                    [
                                        'offline_access',
                                        'openid',
                                        'profile',
                                        'email',
                                        'accounting.invoices',
                                        'accounting.contacts',
                                        'accounting.settings.read',
                                    ].join(' '),
                                )
                                url.searchParams.set('state', state)
                                window.location.href = url.toString()
                            } catch {
                                // Re-enable the button if we never made it to the redirect.
                                setLoading(false)
                            }
                        }}
                    >
                        Connect
                    </Button>
                )}
            </YStack>
        </View>
    )
}

export default () => {
    const integrationsApi = useOrgIntegrationsApi()

    if (!integrationsApi.data) return <Loading />

    return (
        <>
            <Xero />
            {/*<Docusign />*/}
        </>
    )
}
